import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, findCurrency, fmtMoney } from "@/lib/api";
import { LIST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileText, Trash2, Loader2, PlusCircle } from "lucide-react";

export default function ProposalsList() {
  const [items, setItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/proposals"),
        api.get("/currencies"),
      ]);
      setItems(pRes.data);
      setCurrencies(cRes.data.currencies);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await api.delete(`/proposals/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Proposal deleted");
    } catch {
      toast.error("Unable to delete");
    }
  };

  return (
    <div
      data-testid={LIST.page}
      className="max-w-6xl mx-auto px-6 lg:px-10 py-14"
    >
      <div className="flex items-end justify-between gap-6 flex-wrap border-b border-border pb-6">
        <div>
          <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-3">
            Saved
          </p>
          <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter">
            My proposals
          </h1>
        </div>
        <Link to="/proposals/new">
          <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <PlusCircle aria-hidden="true" className="w-4 h-4 mr-2" /> New proposal
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="py-16 flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading proposals…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid={LIST.empty}
          className="mt-14 rounded-3xl border border-dashed border-border p-14 text-center bg-card"
        >
          <FileText
            aria-hidden="true"
            className="w-10 h-10 text-primary mx-auto"
          />
          <h2 className="mt-4 font-display text-2xl tracking-tight">
            No proposals yet
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
            Generate your first business loan or grant proposal and save it to
            revisit, edit or export at any time.
          </p>
          <Link to="/proposals/new">
            <Button className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Draft your first proposal
            </Button>
          </Link>
        </div>
      ) : (
        <div className="mt-10 rounded-2xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/60">
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => {
                const cur = findCurrency(currencies, p.currency_code);
                return (
                  <TableRow
                    key={p.id}
                    data-testid={LIST.row(p.id)}
                    className="hover:bg-muted/40"
                  >
                    <TableCell className="font-medium">
                      <Link
                        to={`/proposals/${p.id}`}
                        data-testid={LIST.view(p.id)}
                        className="hover:text-primary"
                      >
                        {p.title}
                      </Link>
                      <div className="text-xs text-muted-foreground mt-1">
                        {p.business_name}
                        {p.industry_id ? ` · ${p.industry_id}` : ""}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          p.type === "loan"
                            ? "border-primary/30 text-primary"
                            : "border-secondary/30 text-secondary"
                        }
                      >
                        {p.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {fmtMoney(p.amount, cur)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={LIST.delete(p.id)}
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(p.id)}
                        className="rounded-full text-destructive hover:text-destructive"
                      >
                        <Trash2 aria-hidden="true" className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
